import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { SUPER_ADMIN } from "assets/constants/Constants";
import { EDIT_ICON, TRASH_ICON } from "assets/constants/Icons";
import Spinner from "components/Spinner";
import MaterialTable from "material-table";
import { hasPermission } from "components/Permission";
import { getUserList, getRoleListCall } from "lib/axiosUtils";
import { ThemeProvider } from "@mui/styles";
import { createTheme } from "@mui/material";
import ResetPasswordComponent from "components/ResetPasswordComponent";
import DeleteUserComponent from "components/DeleteUserComponent";
import "./user-style.css";

const UserPage = () => {
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [openResetPasswordModal, setOpenResetPasswordModal] = useState(false);
    const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
    const [selectedUserData, setSelectedUserData] = useState(null);

    const currentUserRole = JSON.parse(localStorage.getItem("userRole"));
    const userDetail = JSON.parse(localStorage.getItem("userData"));

    const mytheme = useMemo(() => createTheme({}), []);
    const buttonText = "Add User";

    const getMetadata = () => {
        if (localStorage.getItem("metadata")) {
            return JSON.parse(localStorage.getItem("metadata"));
        }
        return [];
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [userDataRes, roleData] = await Promise.all([
                    getUserList({}, {}),
                    getRoleListCall({}, {}),
                ]);

                const metaData = getMetadata();
                const updatedUserList = setUserData(
                    userDataRes.data.result,
                    metaData
                );

                setUserList(updatedUserList);
                setRoleList(roleData.data.result);
                setIsLoading(false);
            } catch (error) {
                console.log("Error occurred while fetching data", error);
            }
        };

        fetchData();
    }, [currentUserRole]);

    const setUserData = (userList, metaData) => {
        return userList.map((userItem) => {
            const findRegion =
                metaData?.[0]?.meta_data?.regions?.find(
                    (findItem) => findItem.region_id === userItem.region_id
                );

            if (findRegion) {
                return { ...userItem, region_name: findRegion.region };
            }
            return userItem;
        });
    };

    const addEditUserHandler = (item) => {
        if (item) {
            const rowItem = {
                _id: item.userId,
                first_name: item.firstName,
                last_name: item.lastName,
                email_id: item.emailId,
                phone_number: item.phoneNumber,
                username: item.username,
                city_id: item.userCity,
                password: item.password,
                role: item.roleId,
                region_id: item.userRegion,
                referred_code: item.userReferredCode,
                referral_code: item.userReferralCode,
            };

            history.push({
                pathname: "/create-user-page",
                state: { ...rowItem, isFromProfile: false },
            });
        } else {
            history.push({
                pathname: "/create-user-page",
                state: null,
            });
        }
    };

    const renderRole = useCallback(
        (roleId) => {
            return roleList
                .filter((r) => r._id === roleId)
                .map((r) => r.user_role)
                .join(", ");
        },
        [roleList]
    );

    const handleDelete = (rowData) => {
        setSelectedUserData(rowData);
        setOpenDeleteUserModal(true);
    };

    const cancelUserHandle = () => {
        setOpenDeleteUserModal(false);
    };

    const cancelHandle = () => {
        setOpenResetPasswordModal(false);
    };

    return (
        <>
            {isLoading ? (
                <Spinner />
            ) : (
                <div className="main-content user-page-outer-wrapper">
                    <div className="list-wrapper">
                        <div className="back">
                            <ul className="breadcrumb1">
                                <li>User Master</li>
                            </ul>
                        </div>

                        {hasPermission("user", "create") && (
                            <div className="user-button-view">
                                <button
                                    className="brand-button"
                                    onClick={() => addEditUserHandler(null)}
                                >
                                    {buttonText}
                                </button>
                            </div>
                        )}

                        <div
                            className="mt-4 border list-table-div site-table"
                            style={{ marginBottom: 50 }}
                        >
                            {userList.length > 0 ? (
                                <ThemeProvider theme={mytheme}>
                                    <MaterialTable
                                        columns={[
                                            {
                                                title: "Name", field: "user",
                                                cellStyle: { cellWidth: '18%', fontWeight: 'bolder', color: '#000000', fontSize: 14 }
                                            },
                                            {
                                                title: "Email Id", field: "emailId",
                                                cellStyle: { cellWidth: '20%', color: '#808080', fontSize: 14, textTransform: 'none' },
                                                sorting: false
                                            },
                                            {
                                                title: "Phone Number", field: "phoneNumber",
                                                cellStyle: { cellWidth: '12%', color: '#808080', fontSize: 14 },
                                                sorting: false
                                            },
                                            {
                                                title: "Region", field: "region_name",
                                                cellStyle: { cellWidth: '12%', color: '#808080', fontSize: 14, textTransform: 'none' }
                                            },
                                            {
                                                title: "Role", field: "userRole",
                                                cellStyle: { cellWidth: '14%', color: '#808080', fontSize: 14 }
                                            },
                                            {
                                                title: "Subscription", field: "userSubscription",
                                                cellStyle: { cellWidth: '14%', color: '#808080', fontSize: 14 }
                                            }
                                        ].filter(Boolean)}
                                        data={
                                            userList.map((item, index) => {
                                                return (
                                                    {
                                                        user: `${item.first_name} ${item.last_name}`,
                                                        firstName: item.first_name,
                                                        lastName: item.last_name,
                                                        emailId: item.email_id,
                                                        phoneNumber: (currentUserRole.user_role === SUPER_ADMIN) ? item.phone_number : '***',
                                                        username: item.username,
                                                        userCity: item.city_id,
                                                        userRole: renderRole(item.role),
                                                        password: item.password,
                                                        confirmPassword: item.password,
                                                        userId: item._id,
                                                        roleId: item.role,
                                                        isAudit: item.audit_enable,
                                                        userRegion: item.region_id,
                                                        region_name: item.region_name,
                                                        userSubscription: item.subscription_id?.subscription_name,
                                                        userReferredCode: item.referred_code,
                                                        userReferralCode: item.referral_code,
                                                    }
                                                )
                                            })
                                        }

                                        actions={[
                                            hasPermission("user", "update") && (
                                                {
                                                    icon: () => <i className={`${EDIT_ICON}`} title="Edit User"></i>,
                                                    onClick: (event, rowData) =>
                                                        addEditUserHandler(rowData)
                                                }
                                            ),
                                            hasPermission("user", "delete") && (
                                                {
                                                    icon: () => <i className={`${TRASH_ICON}`} title="Delete User"></i>,
                                                    onClick: (event, rowData) =>
                                                        handleDelete(rowData)
                                                }
                                            )
                                        ].filter(Boolean)}
                                        options={{
                                            actionsColumnIndex: -1,
                                            headerStyle: {
                                                color: '#999998',
                                                fontWeight: 'bolder',
                                                fontSize: 15
                                            },
                                            filtering: true,
                                            sorting: true,
                                            columnsButton: true,
                                            exportButton: true,
                                            exportFileName: "User Data"
                                        }}
                                    />
                                </ThemeProvider>
                            ) : (
                                <div className="text-center pt-2">
                                    <label>User records not found!</label>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {openResetPasswordModal && (
                <ResetPasswordComponent modelOpen={openResetPasswordModal} closeModel={cancelHandle} userData={selectedUserData} />
            )}

            {openDeleteUserModal && (
                <DeleteUserComponent modelOpen={openDeleteUserModal} closeModel={cancelUserHandle} userData={selectedUserData} userId={userDetail?._id} />
            )}
        </>
    );
};

export default UserPage;